import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Dropdown, Spinner, Alert, Tooltip, OverlayTrigger, Pagination 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { STATUT_FACTURE, MODE_REGLEMENT } from "../utils/constantes";
import './Factures.css';
import { API_BASE_URL } from "../constants/api";

export default function Factures() {
    const navigate = useNavigate();
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_facture', direction: 'desc' });
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });
    
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchFactures();
        fetchStats();
        fetchTaxes();
    }, [filtreStatut]);

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchFactures = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/factures.php`;
            
            if (filtreStatut !== 'tous') {
                url += `?statut=${filtreStatut}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFactures(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getStatutBadge = (statut) => {
        const config = STATUT_FACTURE[statut] || STATUT_FACTURE.brouillon;
        const colors = {
            success: '#10b981',
            warning: '#f59e0b',
            danger: '#ef4444',
            info: '#3b82f6',
            secondary: '#64748b'
        };
        return (
            <span className="status-badge" style={{ 
                background: `${colors[config.color]}15`, 
                color: colors[config.color] 
            }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const getJoursRetard = (dateEcheance) => {
        const today = new Date();
        const echeance = new Date(dateEcheance);
        const diffTime = today - echeance;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const filteredFactures = useMemo(() => {
        return factures
            .filter(f => 
                f.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.code_client?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_facture') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_facture) - new Date(b.date_facture)
                        : new Date(b.date_facture) - new Date(a.date_facture);
                }
                if (sortConfig.key === 'montant') {
                    return sortConfig.direction === 'asc'
                        ? a.net_a_payer - b.net_a_payer
                        : b.net_a_payer - a.net_a_payer;
                }
                return 0;
            });
    }, [factures, filterText, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredFactures.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredFactures.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading && !factures.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des factures...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error && !factures.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchFactures}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-file-list-3-line fs-20 text-primary"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Gestion des factures</h4>
                                    <p className="text-muted small mb-0">Gérez vos factures, suivez les paiements et les échéances</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                               style={{background:'#1DACCE',border:'0px'}}
                                onClick={() => navigate('/ventes/factures/nouveau')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-add-line"></i>
                                Nouvelle facture
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon"><i className="ri-file-list-3-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Factures (mois)</span>
                                        <h3 className="kpi-value">{stats.mois?.nb_factures || 0}</h3>
                                        <span className="kpi-unit">{formatCurrency(stats.mois?.total_ca || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon"><i className="ri-check-double-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Encaissé</span>
                                        <h3 className="kpi-value">{formatCurrency(stats.mois?.total_regle || 0)}</h3>
                                        <span className="kpi-unit">Réglé</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon"><i className="ri-time-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Impayés</span>
                                        <h3 className="kpi-value">{stats.impayes?.nb || 0}</h3>
                                        <span className="kpi-unit">{formatCurrency(stats.impayes?.total || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-info">
                                    <div className="kpi-icon"><i className="ri-bar-chart-2-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">TVA</span>
                                        <h3 className="kpi-value">{formatCurrency(stats.mois?.total_ca * 0.18 || 0)}</h3>
                                        <span className="kpi-unit">{taxes.tva_active ? `${taxes.tva_taux}%` : 'Inactive'}</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="5">
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher facture, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select 
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="rounded-pill px-3"
                                    >
                                        <option value="tous">Tous les statuts</option>
                                        <option value="brouillon">Brouillons</option>
                                        <option value="emise">Émises</option>
                                        <option value="payee_partielle">Paiements partiels</option>
                                        <option value="payee">Payées</option>
                                        <option value="impayee">Impayées</option>
                                    </Form.Select>
                                </Col>
                                <Col md="4">
                                    <div className="text-end">
                                        <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                            <i className="ri-stack-line me-1"></i>
                                            {filteredFactures.length} facture(s)
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des factures */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">N° Facture</th>
                                            <th>Date</th>
                                            <th>Client</th>
                                            <SortableHeader field="montant" className="text-end">Montant TTC</SortableHeader>
                                            <th className="text-end">Réglé</th>
                                            <th className="text-end">Reste</th>
                                            <th>Échéance</th>
                                            <th>Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" />
                                                </td>
                                            </tr>
                                        ) : currentItems.length > 0 ? (
                                            currentItems.map((facture) => {
                                                const reste = facture.net_a_payer - (facture.montant_regle || 0);
                                                const joursRetard = getJoursRetard(facture.date_echeance);
                                                const estEnRetard = joursRetard > 0 && facture.statut !== 'payee';
                                                
                                                return (
                                                    <tr key={facture.id_facture} className={estEnRetard ? 'table-danger' : ''}>
                                                        <td className="ps-4 fw-semibold">
                                                            <Link to={`/ventes/facturesDetails/${facture.id_facture}`} className="table-link">
                                                                {facture.numero_facture}
                                                            </Link>
                                                        </td>
                                                        <td className="text-muted">{formatDate(facture.date_facture)}</td>
                                                        <td>
                                                            <div className="fw-semibold">{facture.client_nom}</div>
                                                            <small className="text-muted">{facture.code_client}</small>
                                                        </td>
                                                        <td className="text-end fw-bold">{formatCurrency(facture.net_a_payer)}</td>
                                                        <td className="text-end text-success">{formatCurrency(facture.montant_regle || 0)}</td>
                                                        <td className={`text-end ${reste > 0 ? 'text-danger' : 'text-success'}`}>{formatCurrency(reste)}</td>
                                                        <td>
                                                            <div className="fw-semibold">{formatDate(facture.date_echeance)}</div>
                                                            {estEnRetard && <small className="text-danger">Retard J+{joursRetard}</small>}
                                                        </td>
                                                        <td>{getStatutBadge(facture.statut)}</td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <ActionButton
                                                                    variant="outline-info"
                                                                    icon="ri-eye-line"
                                                                    onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
                                                                    tooltip="Voir détails"
                                                                />
                                                                {facture.statut !== 'payee' && facture.statut !== 'annulee' && (
                                                                    <ActionButton
                                                                        variant="outline-success"
                                                                        icon="ri-bank-card-line"
                                                                        onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
                                                                        tooltip="Encaisser"
                                                                    />
                                                                )}
                                                                <ActionButton
                                                                    variant="outline-warning"
                                                                    icon="ri-file-copy-line"
                                                                    onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
                                                                    tooltip="Créer un avoir"
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <p className="text-muted mb-0">Aucune facture trouvée</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small">
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredFactures.length)} sur {filteredFactures.length} facture(s)
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #0dcaf0; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e8f0fe; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                .btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }
                .btn-outline-warning:hover {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                    border-color: #f59e0b;
                    color: white;
                }
                
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </React.Fragment>
    );
}